import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {IVectorizedBatchImageModel, VectorizedStatus} from "../../types/vectorization";
import "./VectorizationBatch.scss";
import {Badge, Button, Card, Form, Modal, OverlayTrigger, Spinner, Tooltip} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload, faStar} from "@fortawesome/free-solid-svg-icons";
import {useLoadOptions} from "../../hooks/useLoadOptions";
import {downloadVectorizedImage} from "../../utils/fileUtil";
import {vectorizationSubmitFeedback} from "../../services/vectorization/vectorizationSubmitFeedback";
import {ToastSuccess} from "../../utils/toastUtil";

interface IVectorizedImagesProps {
    vectorizedImages: IVectorizedBatchImageModel[];
    inputKey: string;
    inputFileName: string;
}

const BatchVectorizedImages: React.FC<IVectorizedImagesProps> = ({vectorizedImages}) => {
    const {t} = useTranslation();
    const {presetOptions, statusOptions} = useLoadOptions();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDisplayFeedbackModal, setIsDisplayFeedbackModal] = useState<boolean>(false);
    const [selectedImageDisplay, setSelectedImageDisplay] = useState<IVectorizedBatchImageModel | null>(null);
    const [selectedImageFeedback, setSelectedImageFeedback] = useState<IVectorizedBatchImageModel | null>(null);
    const [selectedRating, setSelectedRating] = useState<number>(0);
    const [feedbackText, setFeedbackText] = useState<string>("");

    const openFeedbackModal = (image: IVectorizedBatchImageModel) => {
        setSelectedImageFeedback(image);
        setSelectedRating(image.feedbackRating || 0);
        setFeedbackText(image.feedbackText || "");
        setIsDisplayFeedbackModal(true);
    };

    const submitFeedback = async () => {
        if (selectedImageFeedback) {
            setIsLoading(true);
            const response = await vectorizationSubmitFeedback({
                id: selectedImageFeedback.id,
                feedbackText: feedbackText,
                feedbackRating: selectedRating
            });
            if (response) {
                ToastSuccess(t("ExtractedImages.FeedbackSubmitted"))
            }
            vectorizedImages.map((vectorizedImage) => {
                if (vectorizedImage.id === selectedImageFeedback.id) {
                    vectorizedImage.feedbackRating = selectedRating;
                    vectorizedImage.feedbackText = feedbackText;
                }
                return vectorizedImage;
            })
        }

        setIsLoading(false);
        setIsDisplayFeedbackModal(false);
    };

    return (
        <>
            {vectorizedImages.map((vectorizedImage, index) => (
                <div key={index} className="position-relative pt-3">
                    <div className="position-absolute z-1 vector-info-badge">
                        {vectorizedImage.svgStrokeCount &&
                            <Badge bg="primary" className="stroke">
                                {vectorizedImage.svgStrokeCount} {t("Vectorization.Strokes")}
                            </Badge>
                        }
                        <Badge bg={statusOptions[vectorizedImage.status]?.color}>
                            {t(statusOptions[vectorizedImage.status]?.label)}
                        </Badge>
                    </div>
                    <Card className="card-wrap-image">
                        <Card.Body className="d-flex flex-column justify-content-center align-items-center gap-2">
                            <Card.Img
                                className={vectorizedImage.status === VectorizedStatus.COMPLETED ? "pointer-hover" : ""}
                                variant="top"
                                src={vectorizedImage.url}
                                alt={vectorizedImage.id}
                                onClick={() => setSelectedImageDisplay(vectorizedImage)}
                            />
                            {vectorizedImage.status === VectorizedStatus.COMPLETED &&
                                <>
                                    <OverlayTrigger
                                        trigger={["hover", "focus"]}
                                        placement="right"
                                        overlay={<Tooltip>{presetOptions[vectorizedImage.preset]?.tooltip}</Tooltip>}>
                                        <Badge bg="secondary">
                                            {t(presetOptions[vectorizedImage.preset]?.label) || "Raw SVG"}
                                            <br/>
                                        </Badge>
                                    </OverlayTrigger>
                                    {vectorizedImage.feedbackRating !== null &&
                                        <div className="d-flex justify-content-center gap-1">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <FontAwesomeIcon
                                                    key={star}
                                                    icon={faStar}
                                                    className={`${(vectorizedImage.feedbackRating && vectorizedImage.feedbackRating >= star) ? "text-warning" : "text-muted"}`}
                                                    aria-label={`Rate ${star} stars`}
                                                />
                                            ))}
                                        </div>
                                    }
                                </>
                            }
                        </Card.Body>
                        {vectorizedImage.status === VectorizedStatus.COMPLETED && (
                            <Card.Footer className="d-flex flex-row gap-2 justify-content-center">
                                <Button
                                    className="d-flex flex-row gap-1 align-items-baseline"
                                    variant="secondary"
                                    disabled={vectorizedImage.status !== VectorizedStatus.COMPLETED}
                                    onClick={() => downloadVectorizedImage(vectorizedImage.url, vectorizedImage.fileName)}
                                >
                                    {t("Vectorization.Download")}
                                    <FontAwesomeIcon icon={faDownload}/>
                                </Button>
                                <Button
                                    variant="secondary"
                                    disabled={vectorizedImage.status !== VectorizedStatus.COMPLETED}
                                    onClick={() => openFeedbackModal(vectorizedImage)}
                                >
                                    {t("Vectorization.Rating")}
                                </Button>
                            </Card.Footer>
                        )}
                    </Card>
                </div>
            ))}
            <Modal show={isDisplayFeedbackModal} onHide={() => setIsDisplayFeedbackModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Feedback</Modal.Title>
                </Modal.Header>
                <Modal.Body className="d-flex flex-column gap-5">
                    <div className="d-flex justify-content-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <FontAwesomeIcon
                                key={star}
                                icon={faStar}
                                size="2x"
                                className={`cursor-pointer ${selectedRating >= star ? "text-warning" : "text-muted"}`}
                                onClick={() => setSelectedRating(star)}
                            />
                        ))}
                    </div>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder={t("Vectorization.FeedbackTextPlaceHolder")}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={submitFeedback} disabled={selectedRating === 0}>
                        {isLoading ?
                            (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status"
                                             aria-hidden="true"/>
                                    {' '}{t("DropZone.loading")}
                                </>
                            ) : t("Vectorization.SendFeedback")
                        }
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={selectedImageDisplay !== null} onHide={() => setSelectedImageDisplay(null)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t("Image Preview")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <img src={selectedImageDisplay?.url} alt={selectedImageDisplay?.id} className="img-fluid"/>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default BatchVectorizedImages;
